import { Link } from "react-router-dom";
import { Activity, Brain, Wrench, ShieldCheck, Server, DatabaseBackup, ArrowRight, Lock, Gauge, FileCheck } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { PricingCards } from "@/pages/site/Pricing";

const HERO = "https://images.unsplash.com/photo-1763128516808-785e80c1dd68?ixlib=rb-4.1.0&auto=format&fit=crop&w=1400&q=70";

const FEATURES = [
  [Activity, "Endpoint & server monitoring", "CPU, memory, disk, services, event logs, patches and security state from a lightweight Windows agent.", "md:col-span-2"],
  [Brain, "AI troubleshooting", "Claude-powered root-cause analysis with evidence, confidence and a validated playbook recommendation.", ""],
  [Wrench, "Safe automated repairs", "Only signed, pre-approved playbooks run. Every action is risk-rated, logged and verified.", ""],
  [ShieldCheck, "Security-first by design", "Tenant isolation, MFA for admins, signed tasks, replay protection and a tamper-evident audit trail.", "md:col-span-2"],
];

const FAQ = [
  ["Does CodeRed let AI run commands on my computers?", "No. The AI can only recommend a playbook from an approved library. The backend independently validates the playbook, parameters, permissions, risk level and your policy before a cryptographically signed task is sent to the agent."],
  ["Which operating systems are supported?", "Windows 10/11 workstations and Windows Server 2019/2022/2025 are the initial targets. Linux server support is planned for a later phase."],
  ["What data does the agent collect?", "Device health metrics, service states, relevant event-log entries, patch, security and backup service status, and installed software. It never collects documents, browser content or passwords."],
  ["Can I stop all automation instantly?", "Yes. An emergency stop switch disables all corrective actions for your organization immediately. Read-only diagnostics continue."],
  ["Is there a trial?", "Yes — a 15-day trial for up to 10 devices with AI diagnostics and auto-remediation included. Endpoints used during the trial are billed per day on your first statement; after that it's $200 per endpoint per month, billed quarterly."],
];

export default function Home() {
  return (
    <div>
      <section className="relative bg-gray-900 text-white overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0 grid-bg" />
        <div className="relative max-w-7xl mx-auto px-6 py-20 lg:py-28 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7 fade-up">
            <div className="eyebrow mb-6">CodeRed AI IT Autopilot</div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.05]">Meet Your AI IT Administrator.</h1>
            <p className="mt-6 text-base md:text-lg text-gray-300 max-w-xl">CodeRed continuously monitors your computers and servers, diagnoses IT problems and automatically resolves approved routine issues—helping your business reduce downtime and IT support costs.</p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/register" className="px-6 py-3 bg-red-600 hover:bg-red-700 font-semibold inline-flex items-center gap-2" data-testid="hero-start-trial-btn">Start 15-Day Trial <ArrowRight className="w-4 h-4" /></Link>
              <Link to="/contact?topic=demo" className="px-6 py-3 border border-gray-600 hover:border-white hover:text-white text-gray-200 font-semibold" data-testid="hero-book-demo-btn">Book a Demo</Link>
            </div>
          </div>
          <div className="lg:col-span-5 fade-up" style={{ animationDelay: "0.15s" }}>
            <div className="border border-gray-700 bg-gray-950/80">
              <img src={HERO} alt="IT administrator monitoring systems" className="w-full h-56 object-cover opacity-70" />
              <div className="p-5 font-mono text-xs text-gray-300 space-y-1.5">
                <div><span className="text-amber-400">DETECT</span> FIN-WS-014 · service 'Spooler' Stopped</div>
                <div><span className="text-blue-400">DIAGNOSE</span> root cause: driver crash (conf 0.82)</div>
                <div><span className="text-gray-400">POLICY</span> PB-SVC-RESTART · L2 · auto-approved</div>
                <div><span className="text-green-400">VERIFY</span> Spooler == Running ✓ incident resolved</div>
                <div className="text-gray-500 pt-2">Illustrative workflow example</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 py-24" data-testid="features-section">
        <div className="eyebrow mb-3">Product overview</div>
        <h2 className="text-3xl md:text-4xl font-black max-w-2xl">Monitor, diagnose, repair, verify — in one controlled loop.</h2>
        <div className="mt-12 grid md:grid-cols-3 gap-6">
          {FEATURES.map(([Icon, t, d, span]) => (
            <div key={t} className={`card-flat p-8 lift ${span}`}>
              <Icon className="w-6 h-6 text-red-600" />
              <h3 className="mt-5 text-lg font-bold">{t}</h3>
              <p className="mt-2 text-sm text-gray-600 leading-relaxed">{d}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-gray-50 border-y border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-24 grid lg:grid-cols-2 gap-16">
          <div>
            <div className="eyebrow mb-3">Automated maintenance</div>
            <h2 className="text-3xl font-black">A remediation library built around risk.</h2>
            <p className="mt-4 text-gray-600">Every playbook has prechecks, timeouts, retry limits, verification and rollback notes. You decide what runs automatically.</p>
            <div className="mt-8 space-y-3 text-sm">
              {[["L1", "Read-only diagnostics", "Runs under policy"], ["L2", "Low-risk fixes", "Service restart, temp cleanup, DNS flush"], ["L3", "Moderate", "Maintenance window or approval"], ["L4", "High-risk", "Always explicit human approval"]].map(([l, t, d]) => (
                <div key={l} className="flex gap-4 items-start border-l-2 border-red-600 pl-4 py-1"><span className="font-mono font-bold text-red-600 w-6">{l}</span><div><div className="font-semibold">{t}</div><div className="text-gray-500">{d}</div></div></div>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            {[[Server, "Windows Server 2019–2025"], [Gauge, "Health dashboards"], [DatabaseBackup, "Backup health checks"], [FileCheck, "Patch & reboot status"], [Lock, "MFA & RBAC"], [ShieldCheck, "Emergency stop"]].map(([I, t]) => (
              <div key={t} className="card-flat p-6"><I className="w-5 h-5 text-gray-900" /><div className="mt-3 font-semibold text-sm">{t}</div></div>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 py-24" data-testid="benefits-section">
        <div className="grid md:grid-cols-3 gap-10">
          {[["Less downtime", "Routine failures like stopped services or full disks are caught and fixed before users notice."], ["Lower support load", "Technicians focus on real problems; CodeRed handles the repetitive first response."], ["Full accountability", "Every detection, decision and action is recorded with who or what initiated it."]].map(([t, d]) => (
            <div key={t}><h3 className="text-xl font-bold">{t}</h3><p className="mt-2 text-gray-600 text-sm">{d}</p></div>
          ))}
        </div>
      </section>

      <section className="bg-gray-900 text-white" data-testid="pricing-preview">
        <div className="max-w-7xl mx-auto px-6 py-24">
          <div className="eyebrow mb-3">Pricing</div>
          <h2 className="text-3xl font-black mb-10">Simple per-endpoint pricing.</h2>
          <PricingCards dark />
        </div>
      </section>

      <section className="max-w-3xl mx-auto px-6 py-24" data-testid="faq-section">
        <h2 className="text-3xl font-black mb-8">Frequently asked questions</h2>
        <Accordion type="single" collapsible>
          {FAQ.map(([q, a], i) => (
            <AccordionItem key={q} value={`q${i}`}>
              <AccordionTrigger data-testid={`faq-q-${i}`} className="text-left">{q}</AccordionTrigger>
              <AccordionContent className="text-gray-600">{a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      <section className="border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-20 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <h2 className="text-3xl font-black max-w-xl">Put your first Windows device under CodeRed in minutes.</h2>
          <Link to="/register" className="px-6 py-3 bg-red-600 text-white font-semibold hover:bg-red-700" data-testid="cta-register-btn">Create your organization</Link>
        </div>
      </section>
    </div>
  );
}
